import { OwnableProduct_ProductType as ProductType } from '__generated__/graphql-types';
import { branch, compose, mapProps, withProps } from 'recompose';

import type { EnrollmentChoices } from 'bundles/enroll-common/components/xdp/withEnrollment';
import { createContainer } from 'bundles/naptimejs';
import S12nDerivativesV1 from 'bundles/naptimejs/resources/s12nDerivatives.v1';
import {
  type OwnableProductByLegacyProductId,
  isS12nDerivativesToOwnableProductsEnabled,
  withFindOwnableProductsByLegacyProductIds,
} from 'bundles/ownable-products-common/hooks/useFindOwnableProducts';

export type PropsToWithPrice = {
  enrollmentAvailableChoices?: EnrollmentChoices;
  s12nId?: string;
};

type PropsFromWithPriceNaptime = {
  s12nDerivative?: S12nDerivativesV1;
  multiS12nDerivatives?: Array<S12nDerivativesV1>;
};

type PropsFromWithPriceOwnableProducts = {
  ownableProducts?: Array<OwnableProductByLegacyProductId>;
};

export type PropsFromWithPrice = PropsFromWithPriceNaptime & PropsFromWithPriceOwnableProducts;

type LegacyProductId = { productType: ProductType; productItemId: string };

const withPrice = <InputProps extends PropsToWithPrice>(isNotRequired?: boolean) =>
  compose<PropsFromWithPrice, InputProps>(
    branch(
      () => isS12nDerivativesToOwnableProductsEnabled(),
      compose(
        withProps<InputProps, InputProps & { legacyProductIds: Array<LegacyProductId>; skip: boolean }>(
          ({ enrollmentAvailableChoices, s12nId }) => {
            if (!enrollmentAvailableChoices) {
              return { ...({ enrollmentAvailableChoices, s12nId } as InputProps), legacyProductIds: [], skip: true };
            }

            let legacyProductIds: Array<LegacyProductId> = [];

            // if user can subscribe to multiple s12ns but can't do free trial for any s12n,
            // retrieve the price for every s12n
            if (
              enrollmentAvailableChoices.isMixAndMatch &&
              !enrollmentAvailableChoices.canEnrollThroughS12nSubscriptionFreeTrial
            ) {
              legacyProductIds = enrollmentAvailableChoices.mixAndMatchAvailableS12nSubscriptions.map((product) => ({
                productType: ProductType.ProductTypeSpecializationSubscription,
                productItemId: product.productId.productItemId,
              }));
            } else if (enrollmentAvailableChoices.canSubscribeToS12n && s12nId) {
              legacyProductIds = [
                {
                  productType: ProductType.ProductTypeSpecializationSubscription,
                  productItemId: enrollmentAvailableChoices.availableS12nSubscriptions?.[0]?.productItemId as string,
                },
              ];
            }

            return {
              ...({ enrollmentAvailableChoices, s12nId } as InputProps),
              legacyProductIds,
              skip: legacyProductIds.length === 0,
            };
          }
        ),
        withFindOwnableProductsByLegacyProductIds
      ),
      compose(
        createContainer<PropsFromWithPriceNaptime, InputProps>(({ enrollmentAvailableChoices, s12nId }) => {
          if (!enrollmentAvailableChoices) {
            return {};
          }
          // if user can subscribe to multiple s12ns but can't do free trial for any s12n,
          // retrieve the price for every s12n
          if (
            enrollmentAvailableChoices.isMixAndMatch &&
            !enrollmentAvailableChoices.canEnrollThroughS12nSubscriptionFreeTrial
          ) {
            const enrollmentS12nIds = enrollmentAvailableChoices.subscriptionEnrollmentS12nIds;

            return {
              multiS12nDerivatives: S12nDerivativesV1.multiGet(enrollmentS12nIds, {
                fields: ['catalogPrice'],
                required: !isNotRequired,
              }),
            };
          } else if (enrollmentAvailableChoices.canSubscribeToS12n) {
            return {
              // If we can subscribe to the s12n, it follows that we have a s12nId.
              s12nDerivative: S12nDerivativesV1.get(s12nId!, {
                fields: ['catalogPrice'],
                required: !isNotRequired,
              }),
            };
          } else {
            return {
              productPrices: [],
            };
          }
        }),
        mapProps<PropsFromWithPrice, PropsFromWithPriceNaptime>(({ multiS12nDerivatives, ...rest }) => ({
          ...(multiS12nDerivatives && {
            multiS12nDerivatives,
            s12nDerivative: multiS12nDerivatives[0],
          }),
          ...rest,
        }))
      )
    )
  );

export { withPrice };
